import React, { useState, useRef, useEffect, useMemo } from 'react';

// Generic imports
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useDrop } from 'react-dnd';
import pluralize from 'pluralize';

// MUI imports
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import { grey, red } from '@mui/material/colors';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faTrash, faLinkSlash, faCircleDollarToSlot, faPlus, faMagnifyingGlassDollar, faPenToSquare, faSearch
, faInfoCircle, faHandHoldingDollar } from '@fortawesome/pro-light-svg-icons';
import { faCircleDollar } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import currencyToText from '../../CurrencyToText';
import CurrencyDialog from '../../CurrencyDialog.js';
import { formulaToReact, formulaToParams } from '../../utils/Formula.js';
import FormulaEditorModal from '../components/FormulaEditorModal.js';
import { FieldTypes } from '../../FieldTypes.js';
import { parseFloatOrString } from './OpportunityOneOffBuilder.utils.js';
import { calcWeight, calcChargeableWeight, calcWeightOrMeasurment } from '../../CalcWeight.js';
import { DisplayCurrencySettingsField, BulkUpdateSettings } from './DisplayCurrencySettings.js';
import ConfirmDialog from '../components/ConfirmDialog.js';
import { ProductDescription } from '../../utils/ProductHelperComponents.js';
import { getProductShortDescription } from '../utils/product.js';
import { findMatchingProducts, normalizeProductType } from '../utils/product.js';
import { calcFieldValue } from '../../utils/opportunity.js';
import { getTotalRates, convertRateKgToLbsIfNeeded } from '../utils/rates.js';

function isManualBuyingRate(br) {
    return br.platform === -2 || br.platform === undefined || br.platform === null;
}

function FieldSettingsDialog(props) {
    const { open, onClose, field, onDone, onDeleteField, onTitleChange, products, sections, transportationMethod, assignSection,
        organization, exchangeRates, lockedExchangeRates, suppliers, buyingRates = [], offers = [], sx, initialCategory, hideSidePanel } = props;

    const [ selectedCategory, setSelectedCategory ] = useState(initialCategory || 'selling');
    const [ innerField, setInnerField ] = useState(_.cloneDeep(field));
    const [ confirmDeleteOpen, setConfirmDeleteOpen ] = useState(false);
    const [ buyingRatesRows, setBuyingRatesRows ] = useState([]);
    const [ newBuyingRates, setNewBuyingRates ] = useState([]);
    const [ section, setSection ] = useState(null);
    const [ validationError, setValidationError ] = useState(null);
    const theme = useTheme();
    const bulkUpdateRef = useRef(null);

    // Products this charge belongs to: per-product selling values are keyed by product id,
    // and flat charges are tied to a product only through their linked costs.
    const associatedProducts = useMemo(() => {
        const productIds = _.uniq([
            ..._.keys(innerField.values),
            ...(innerField.buyingRates || []).map(br => br.opportunityProductId),
        ]);
        return (products || []).filter(p => productIds.includes(p.id));
    }, [innerField.values, innerField.buyingRates, products]);

    useEffect(() => {
        setSelectedCategory(initialCategory || 'selling');

        let setFieldType = field.type;
        const fieldType = FieldTypes[transportationMethod]?.find(ft => ft.value === field.type);
        if (fieldType?.enabled && !fieldType.enabled({ organization })) {
            setFieldType = 'per-unit';
        }

        setInnerField(_.cloneDeep({...field, type: setFieldType}));
        setSection(sections.find(s => s.offers.some(o => o.fields.some(f => f.id === field.id)))?.id || sections[0].id);
        setValidationError(null);

        let brRows = _.compact(field.buyingRates?.map(br => {
            const buyingRate = buyingRates.find(b => b.id === br.rateId);
            if (!buyingRate) return null;
            const buyingRateField = _.find(
                _(buyingRate.offer.sections).map(s => s.offers).flatten().map(o => o.fields).flatten().value(),
                f => f.id === br.fieldId
            );
            const product = products.find(p => p.id === br.opportunityProductId);

            if (!buyingRateField || !product) return null;

            // Mirror getFieldRatesForProduct (the cost center): a cost priced for a specific
            // product type no longer applies once that opportunity product's type is changed
            // (the product id is preserved). Treat it as unlinked here too, so the field-settings
            // popup matches the cost center / Alternative Costs instead of showing the cost still
            // linked to the now-different product.
            if (buyingRate.product?.type && product?.type && normalizeProductType(buyingRate.product.type) !== normalizeProductType(product.type)) return null;

            if (isManualBuyingRate(buyingRate)) {
                const fieldType = FieldTypes[transportationMethod]?.find(ft => ft.value === buyingRateField.type);
                const chargeLines = fieldType?.getChargeLines ? fieldType.getChargeLines({ field: buyingRateField, products: [buyingRate.product], organization }) : [];
                const fieldValueId = !_.isEmpty(chargeLines) ? chargeLines[0].fieldValueId : buyingRateField.type;

                // Try to find the supplier by uniqueId or name, otherwise create a fallback with the saved organization name
                let foundSupplier = null;
                
                // First, try to match by uniqueId if it exists and is not malformed
                if (buyingRate.supplier?.uniqueId && buyingRate.supplier.uniqueId.replace(/:/g, '').trim()) {
                    foundSupplier = suppliers.find(s => s.uniqueId === buyingRate.supplier.uniqueId);
                }
                
                // If not found by uniqueId, try to match by name
                if (!foundSupplier && buyingRate.supplier?.organization) {
                    foundSupplier = suppliers.find(s => s.name.toLowerCase() === buyingRate.supplier.organization.toLowerCase());
                }
                
                // If still not found, create a fallback supplier object with the saved data
                const supplier = foundSupplier || (buyingRate.supplier?.organization ? {
                    name: buyingRate.supplier.organization,
                    uniqueId: buyingRate.supplier.uniqueId,
                    email: buyingRate.supplier.email
                } : null);

                return ({
                    id: buyingRateField.id,
                    type: 'manual',
                    title: buyingRateField.title,
                    fieldType: buyingRateField.type,
                    product: product,
                    supplier: supplier,
                    buyingRateId: buyingRate.id,
                    ...buyingRateField.values[fieldValueId]
                })
            } else {
                return ({
                    id: uuidv4(),
                    type: 'buyingRate',
                    buyingRate: buyingRate,
                    fieldId: buyingRateField.id,
                })
            }
        }));
        brRows = _.uniqBy(brRows, row => `${row.fieldId || row.id}|${row.type}|${row.buyingRate?.id || row.buyingRateId}|${row.product?.id}`)
        setBuyingRatesRows(brRows);
    }, [ field, open ]);

    const totalBuyingPrice = useMemo(() => {
        const { buyingRates: returnedBuyingRates, field: newField } = rowsToRates({ rows: buyingRatesRows, field: innerField, buyingRates: [...(buyingRates || []), ...(newBuyingRates || [])], offers, transportationMethod, products, organization });

        const ret = getTotalRates({ field: newField, fieldBuyingRates: newField.buyingRates, products, buyingRates: returnedBuyingRates, transportationMethod, exchangeRates, organization, skipSumCurrencies: true, lockedExchangeRates });
        return ret;
    }, [ innerField, products, sections, transportationMethod, organization, buyingRates, newBuyingRates, buyingRatesRows, offers, lockedExchangeRates ]);

    const totalSellingPrice = useMemo(() => {
        const { buyingRates: returnedBuyingRates, field: newField } = rowsToRates({ rows: buyingRatesRows, field: innerField, buyingRates: [...(buyingRates || []), ...(newBuyingRates || [])], offers, transportationMethod, products, organization });
        const displayCurrency = innerField.displayCurrency?.convert ? innerField.displayCurrency.to : (_.isString(innerField.displayCurrency) ? innerField.displayCurrency : null);
        return calcFieldValue({ field: {...innerField, displayCurrency}, products, sections, transportationMethod, organization, buyingRates: returnedBuyingRates, fieldBuyingRates: newField.buyingRates, exchangeRates, lockedExchangeRates });
    }, [ innerField, products, sections, transportationMethod, organization, buyingRates, buyingRatesRows, innerField.buyingRates, exchangeRates, lockedExchangeRates ]);

    const totalProfitAndMargin = useMemo(() => {
        const profit = {};
        const margin = {};
        const sellingSum = totalSellingPrice?.sum || {};
        const buyingSum = totalBuyingPrice || {};

        // Calculate profit and margin for each currency that has a selling price
        _.forEach(sellingSum, (sellingValue, currency) => {
            const sellingNum = parseFloat(sellingValue) || 0;
            const buyingNum = parseFloat(buyingSum[currency]) || 0;
            const profitValue = sellingNum - buyingNum;
            profit[currency] = profitValue;
            
            // Calculate margin: (profit / sellingPrice) * 100
            if (sellingNum !== 0 && !isNaN(sellingNum) && !isNaN(buyingNum)) {
                margin[currency] = (profitValue / sellingNum) * 100;
            } else {
                margin[currency] = 0;
            }
        });

        return { profit, margin };
    }, [ totalSellingPrice, totalBuyingPrice ]);

    const handleDialogClose = (event, reason) => {
        // Allow closing via Cancel button (reason will be 'backdropClick' or 'escapeKeyDown' for outside clicks/ESC)
        // But prevent closing if there are invalid buying rates
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            const invalidRows = buyingRatesRows.filter(row => {
                if (row.type === 'manual') {
                    return !row.supplier || (!row.supplier.organization && !row.supplier.name);
                } else if (row.type === 'buyingRate') {
                    return !row.buyingRate || !row.buyingRate.supplier || !row.buyingRate.supplier.organization;
                }
                return false;
            });

            if (invalidRows.length > 0) {
                setValidationError('Please ensure all buying rates have a supplier organization before closing.');
                return;
            }
        }
        setValidationError(null);
        onClose();
    };

    const totalSellingPriceNormalized = useMemo(() => {
        return _.sum(_.map(totalSellingPrice.sum, (value, currency) => value / exchangeRates[currency]));
    }, [ totalSellingPrice, exchangeRates ]);

    const totalBuyingPriceNormalized = useMemo(() => {
        return _.sum(_.map(totalBuyingPrice, (value, currency) => value / exchangeRates[currency]));
    }, [ totalBuyingPrice, exchangeRates ]);

    const defaultCurrency = _.keys(totalSellingPrice.sum)[0] || organization.defaults?.currency;
    let displayCurrency = innerField.displayCurrency;
    if (_.isObject(displayCurrency)) {
        if (displayCurrency.convert) {
            displayCurrency = displayCurrency.to;
        } else {
            displayCurrency = null;
        }
    }
    if (!displayCurrency) displayCurrency = defaultCurrency;

    const totalProfit = useMemo(() => {
        const val = Math.round((totalSellingPriceNormalized - totalBuyingPriceNormalized) * exchangeRates[displayCurrency] * 10000) / 10000;
        if (val === 0) return 0;
        return val;
    }, [ totalSellingPriceNormalized, totalBuyingPriceNormalized, exchangeRates, displayCurrency ]);

    const totalMargin = useMemo(() => {
        return (totalProfit / (totalSellingPriceNormalized * exchangeRates[displayCurrency])) * 100;
    }, [ totalProfit, totalSellingPriceNormalized ]);

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            fullWidth
            maxWidth='xl'
            PaperProps={{ sx: { border: `1px solid ${theme.palette.border1}` } }}
            scroll='body'
            disableEnforceFocus={true}
            disableAutoFocus={true}
            disableRestoreFocus={true}
            sx={sx}
        >
            <DialogContent sx={{ display: 'flex', padding: 0 }}>
                {!hideSidePanel && <Box sx={{
                    width: '300px',
                    backgroundColor: '#f8fafc',
                    borderRight: '1px solid #e2e8f0',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <Typography variant='h6' sx={{ padding: '16px', fontWeight: 600 }}>
                        {field.title ? `${field.title} settings` : 'Field Settings'}
                    </Typography>
                    {!_.isEmpty(associatedProducts) && (
                        <Typography variant='body2' sx={{ padding: '0 16px 12px', marginTop: '-12px', color: '#64748b' }}>
                            {associatedProducts.map(p => getProductShortDescription({ product: p })).join(', ')}
                        </Typography>
                    )}
                    <List sx={{ flexGrow: 1 }}>
                        <ListItem 
                            button 
                            selected={selectedCategory === 'selling'} 
                            onClick={() => setSelectedCategory('selling')}
                            sx={{
                                borderLeft: selectedCategory === 'selling' ? '3px solid #3b82f6' : '3px solid transparent',
                                backgroundColor: selectedCategory === 'selling' ? 'transparent' : 'transparent',
                                '& .MuiListItemIcon-root': {
                                    color: '#64748b'
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faHandHoldingDollar} style={{ marginRight: '10px', color: '#64748b', fontSize: '14px' }} />
                            <ListItemText 
                                primary={<span style={{ fontSize: '13px', fontWeight: 600 }}>Selling Rates</span>} 
                            />
                        </ListItem>
                        <ListItem 
                            button 
                            selected={selectedCategory === 'buying'} 
                            onClick={() => setSelectedCategory('buying')}
                            sx={{
                                borderLeft: selectedCategory === 'buying' ? '3px solid #3b82f6' : '3px solid transparent',
                                backgroundColor: selectedCategory === 'buying' ? 'transparent' : 'transparent',
                                '& .MuiListItemIcon-root': {
                                    color: '#64748b'
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faCircleDollar} style={{ marginRight: '10px', color: '#64748b', fontSize: '14px' }} />
                            <ListItemText 
                                primary={<span style={{ fontSize: '13px', fontWeight: 600 }}>Costs</span>} 
                            />
                        </ListItem>
                        <ListItem 
                            button 
                            selected={selectedCategory === 'currency'} 
                            onClick={() => setSelectedCategory('currency')}
                            sx={{
                                borderLeft: selectedCategory === 'currency' ? '3px solid #3b82f6' : '3px solid transparent',
                                backgroundColor: selectedCategory === 'currency' ? 'transparent' : 'transparent',
                                '& .MuiListItemIcon-root': {
                                    color: '#64748b'
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faCircleDollarToSlot} style={{ marginRight: '10px', color: '#64748b', fontSize: '14px' }} />
                            <ListItemText 
                                primary={<span style={{ fontSize: '13px', fontWeight: 600 }}>Currency</span>} 
                            />
                        </ListItem>
                    </List>
                    <Divider sx={{ borderColor: '#e2e8f0', borderWidth: '1px' }} />
                    <Box sx={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Box>
                            <Typography component='span' sx={{
                                fontSize: '11px',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                color: '#64748b',
                                letterSpacing: '0.5px'
                            }}>
                                Total selling price:
                            </Typography>
                            <Tooltip arrow placement='right' title={(() => {
                                const sellCurrencies = _.keys(totalSellingPrice.sum);
                                const buyCurrencies = _.keys(totalBuyingPrice);
                                const allCurrencies = _.uniq([...sellCurrencies, ...buyCurrencies]);
                                if (allCurrencies.length <= 1) return 'No currency conversion applied';
                                const fmt = (n) => Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(n);
                                const source = innerField.displayCurrency?.source || 'live';
                                const lines = [];
                                for (const from of allCurrencies) {
                                    for (const to of allCurrencies) {
                                        if (from === to) continue;
                                        const orgRate = _.find(organization.exchangeRates, { from, to }) || _.find(organization.exchangeRates, { from: to, to: from });
                                        const orgValue = orgRate ? (orgRate.from === from ? orgRate.rate : 1 / orgRate.rate) : null;
                                        const lockedOrg = lockedExchangeRates?.organization ? (_.find(lockedExchangeRates.organization, { from, to }) || _.find(lockedExchangeRates.organization, { from: to, to: from })) : null;
                                        const lockedOrgValue = lockedOrg ? (lockedOrg.from === from ? lockedOrg.rate : 1 / lockedOrg.rate) : null;
                                        const lockedLive = lockedExchangeRates?.live ? lockedExchangeRates.live[to] / lockedExchangeRates.live[from] : null;
                                        const liveValue = exchangeRates?.[to] && exchangeRates?.[from] ? exchangeRates[to] / exchangeRates[from] : null;
                                        let usedRate, usedSource;
                                        if (source === 'fixed' && lockedOrgValue) { usedRate = lockedOrgValue; usedSource = 'locked workspace'; }
                                        else if (source === 'fixed' && orgValue) { usedRate = orgValue; usedSource = 'workspace'; }
                                        else if (source === 'live' && lockedLive) { usedRate = lockedLive; usedSource = 'locked live'; }
                                        else if (liveValue) { usedRate = liveValue; usedSource = 'live'; }
                                        if (usedRate) lines.push(`1 ${from} = ${fmt(usedRate)} ${to} (${usedSource})`);
                                    }
                                }
                                return lines.length > 0 ? lines.join('\n') : 'No conversion rates available';
                            })()}>
                                <Typography component='span' sx={{
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    marginLeft: '8px',
                                    cursor: 'help',
                                    borderBottom: '1px dashed #94a3b8'
                                }}>
                                    {_.map(totalSellingPrice.sum, (value, currency) => currencyToText(currency, true, value.toLocaleString())).join(', ')}
                                </Typography>
                            </Tooltip>
                        </Box>
                        <Box>
                            <Typography component='span' sx={{ 
                                fontSize: '11px', 
                                textTransform: 'uppercase', 
                                fontWeight: 600, 
                                color: '#64748b',
                                letterSpacing: '0.5px'
                            }}>
                                Total buying price:
                            </Typography>
                            <Typography component='span' sx={{ 
                                fontSize: '13px', 
                                fontWeight: 700, 
                                color: '#0f172a',
                                marginLeft: '8px'
                            }}>
                                {_.map(totalBuyingPrice, (value, currency) => currencyToText(currency, true, value.toLocaleString())).join(', ')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography component='span' sx={{ 
                                fontSize: '11px', 
                                textTransform: 'uppercase', 
                                fontWeight: 600, 
                                color: '#64748b',
                                letterSpacing: '0.5px'
                            }}>
                                Total Profit:
                            </Typography>
                            <Typography component='span' sx={{ 
                                fontSize: '13px', 
                                fontWeight: 700, 
                                color: totalProfit >= 0 ? '#10b981' : '#ef4444',
                                marginLeft: '8px'
                            }}>
                                {currencyToText(displayCurrency, true, totalProfit.toLocaleString())}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography component='span' sx={{ 
                                fontSize: '11px', 
                                textTransform: 'uppercase', 
                                fontWeight: 600, 
                                color: '#64748b',
                                letterSpacing: '0.5px'
                            }}>
                                Total Margin:
                            </Typography>
                            <Typography component='span' sx={{ 
                                fontSize: '13px', 
                                fontWeight: 700, 
                                color: totalMargin >= 0 ? '#10b981' : '#ef4444',
                                marginLeft: '8px'
                            }}>
                                {totalMargin.toFixed(2)}%
                            </Typography>
                        </Box>
                    </Box>
                </Box>}
                <Box sx={{ flexGrow: 1, padding: '32px', backgroundColor: '#ffffff', overflowY: 'auto' }}>
                    {validationError && (
                        <Alert severity='error' sx={{ marginBottom: '16px' }} onClose={() => setValidationError(null)}>
                            {validationError}
                        </Alert>
                    )}
                    {selectedCategory === 'selling' && (
                        <FieldSettingSellingRates
                            field={innerField} 
                            setField={setInnerField} 
                            products={products} 
                            sections={sections} 
                            section={section} 
                            setSection={setSection} 
                            transportationMethod={transportationMethod} 
                            organization={organization} 
                            assignSection={assignSection}
                            buyingRates={buyingRates}
                            newBuyingRates={newBuyingRates}
                            buyingRatesRows={buyingRatesRows}
                            exchangeRates={exchangeRates}
                            lockedExchangeRates={lockedExchangeRates}
                            offers={offers}
                        />
                    )}
                    {selectedCategory === 'buying' && (
                        <FieldSettingBuyingRates
                            products={products}
                            sections={sections}
                            transportationMethod={transportationMethod}
                            organization={organization}
                            suppliers={suppliers}
                            rows={buyingRatesRows}
                            setRows={setBuyingRatesRows}
                            buyingRates={buyingRates}
                            newBuyingRates={newBuyingRates}
                            setNewBuyingRates={setNewBuyingRates}
                        />
                    )}
                    {selectedCategory === 'currency' && (
                        <>
                            <DisplayCurrencySettingsField field={innerField} setField={setInnerField} organization={organization} exchangeRates={exchangeRates} lockedExchangeRates={lockedExchangeRates} />
                            <BulkUpdateSettings field={innerField} ref={bulkUpdateRef} />
                        </>
                    )}
                </Box>
            </DialogContent>
            {selectedCategory === 'buying' && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '0 32px 8px' }}>
                    <Typography
                        component='a'
                        onClick={() => {
                            window.dispatchEvent(new CustomEvent('open-sidepane-page', { detail: 'opportunity-buying-rates' }));
                        }}
                        sx={{
                            fontSize: '13px',
                            color: '#3b82f6',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontFamily: 'Poppins, sans-serif',
                            '&:hover': { color: '#2563eb' }
                        }}
                    >
                        View all costs related to the offer here
                    </Typography>
                </Box>
            )}
            <DialogActions sx={{ borderTop: '1px solid #e2e8f0', padding: '24px 32px', justifyContent: 'flex-end', gap: '8px' }}>
                {onDeleteField && (
                    <Button
                        color='error'
                        startIcon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => {
                            setConfirmDeleteOpen(true);
                        }}
                        sx={{ marginRight: 'auto' }}
                    >
                        Delete Field
                    </Button>
                )}
                <Button 
                    onClick={() => {
                        setValidationError(null);
                        onClose();
                    }}
                    sx={{ textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={() => {
                    // Validate that all buying rates have a supplier.organization
                    const invalidRows = buyingRatesRows.filter(row => {
                        if (row.type === 'manual') {
                            // For manual rows, check if supplier has organization or name
                            return !row.supplier || (!row.supplier.organization && !row.supplier.name);
                        } else if (row.type === 'buyingRate') {
                            // For buyingRate rows, check if buyingRate has supplier.organization
                            return !row.buyingRate || !row.buyingRate.supplier || !row.buyingRate.supplier.organization;
                        }
                        return false;
                    });

                    if (invalidRows.length > 0) {
                        setValidationError('Please ensure all buying rates have a supplier organization before saving.');
                        return;
                    }

                    setValidationError(null);
                    const { buyingRates: returnedBuyingRates, offers: newOffers, field: newField } = rowsToRates({ rows: buyingRatesRows, field: innerField, buyingRates: [...(buyingRates || []), ...(newBuyingRates || [])], offers, transportationMethod, products, organization });

                    if (assignSection) {
                        newOffers.forEach(offer => offer.sections.forEach(s => s.offers.forEach(o => {
                            o.fields = o.fields.filter(f => (f.id !== newField.id));
                            if (s.id === section) {
                                o.fields.push(newField);
                            }
                        })));
                    }

                    if (newField.type !== 'string') {
                        Object.values(newField.values).forEach(value => {
                            value.value = parseFloat(value.value);
                        });
                    }
                    if (newField.type === 'custom') {
                        // A count that is still mid-typing, or has been emptied, is not a number,
                        // and the opportunity schema takes a number here without casting — so
                        // sending the quote failed on a message about types that meant nothing to
                        // the person reading it. One item is what no count means. Matches what the
                        // standard builder already does before saving.
                        const parsed = parseFloat(newField.values.custom.multiplier);
                        newField.values.custom.multiplier = isNaN(parsed) ? 1 : parsed;
                    }
                    onDone({ field: newField, section, currencyBulkUpdate: bulkUpdateRef.current?.get(), buyingRates: returnedBuyingRates, offers: newOffers });
                }}
                variant='contained'
                sx={{ textTransform: 'none', fontFamily: 'Poppins, sans-serif' }}
            >
                Save
            </Button>
            </DialogActions>
            <ConfirmDialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} onConfirm={onDeleteField}>
                Are you sure you want to delete this field?
            </ConfirmDialog>
        </Dialog>
    );
}

export default FieldSettingsDialog;

function FieldSettingSellingRates(props) {
    const { field, setField, products, sections, section, setSection, transportationMethod, organization, assignSection,
        buyingRates, newBuyingRates, buyingRatesRows, exchangeRates, lockedExchangeRates, offers } = props;
    const [ currencyDialogOpen, setCurrencyDialogOpen ] = useState(false);
    const [ formulaDialogOpen, setFormulaDialogOpen ] = useState(null); // null, 'uplift', or 'formula'

    const fieldType = FieldTypes[transportationMethod]?.find(ft => ft.value === field.type);

    // Calculate buying rates for formula calculations
    const calculatedBuyingRates = useMemo(() => {
        const { buyingRates: returnedBuyingRates, field: newField } = rowsToRates({ 
            rows: buyingRatesRows, 
            field, 
            buyingRates: [...(buyingRates || []), ...(newBuyingRates || [])], 
            offers, 
            transportationMethod, 
            products, 
            organization 
        });
        return { buyingRates: returnedBuyingRates, field: newField };
    }, [ buyingRatesRows, field, buyingRates, newBuyingRates, offers, transportationMethod, products, organization ]);

    const chargeLines = fieldType?.getChargeLines ? fieldType.getChargeLines({ field: calculatedBuyingRates.field, products, organization, buyingRates: calculatedBuyingRates.buyingRates, fieldBuyingRates: calculatedBuyingRates.field.buyingRates }) : [];

    return (
        <Box>
            <TextField
                fullWidth
                label='Title'
                value={field.title}
                onChange={(e) => {
                    const newTitle = e.target.value;
                    const updatedField = { ...field, title: newTitle };
                    setField(updatedField);
                }}
                margin='normal'
                sx={{ 
                    marginBottom: '12px',
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#ffffff',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                        '& fieldset': {
                            borderColor: '#e2e8f0',
                            borderWidth: '1px'
                        },
                        '&:hover fieldset': {
                            borderColor: '#e2e8f0'
                        },
                        '&.Mui-focused': {
                            '& fieldset': {
                                borderColor: '#3b82f6',
                                borderWidth: '1px'
                            },
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)'
                        }
                    }
                }}
            />

            {assignSection && (
                <FormControl shrink fullWidth sx={{ marginBottom: '12px' }}>
                    <InputLabel shrink>Section</InputLabel>
                    <Select
                        shrink
                        fullWidth
                        label='Section'
                        value={section}
                        onChange={(e) => setSection(e.target.value)}
                        margin='normal'
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#e2e8f0',
                                borderWidth: '1px'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#e2e8f0'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#3b82f6',
                                borderWidth: '1px'
                            },
                            '&.Mui-focused': {
                                boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)'
                            },
                            backgroundColor: '#ffffff',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
                        }}
                    >
                        {sections.map(s => <MenuItem key={s.id} value={s.id}>{s.title === '***UNASSIGNED***' ? 'Unassigned' : s.title}</MenuItem>)}
                    </Select>
                </FormControl>
            )}

            <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                    fullWidth
                    label='Type'
                    value={field.type}
                    onChange={(e) => setField({ ...field, type: e.target.value })}
                    margin='normal'
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0',
                            borderWidth: '1px'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                            borderWidth: '1px'
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)'
                        },
                        backgroundColor: '#ffffff',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
                    }}
                >
                    {FieldTypes[transportationMethod]?.map(ft => (_.isFunction(ft.enabled) && !ft.enabled({ organization })) ? null : <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>)}
                </Select>  
            </FormControl>
            {field.type === 'string' && (
                <TextField
                    fullWidth
                    label='Value'
                    value={field.values.string?.value || ''}
                    onChange={(e) => setField({ ...field, values: { ...field.values, string: { ...field.values.string, value: e.target.value } } })}
                    margin='normal'
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#ffffff',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                            '& fieldset': {
                                borderColor: '#e2e8f0',
                                borderWidth: '1px'
                            },
                            '&:hover fieldset': {
                                borderColor: '#e2e8f0'
                            },
                            '&.Mui-focused': {
                                '& fieldset': {
                                    borderColor: '#3b82f6',
                                    borderWidth: '1px'
                                },
                                boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)'
                            }
                        }
                    }}
                />
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '12px 0px' }}>
                {chargeLines.map(line => {
                    const { title, fieldValueId, productsInChargeLine, quantityMultiplier: multiplier, multiplierText, multiplierTextSingular } = line;
                    const productId = fieldValueId;
                    const formula = _.get(field, `values[${productId}].formula`, null);
                    const value = _.get(field, `values[${productId}].value`, null);
                    const currency = _.get(field, `values[${productId}].currency`, organization.defaults?.currency);

                    const updateAllProducts = (update) => {
                        let newField = _.cloneDeep(field);
                        if (field.type !== 'flat' && field.type !== 'custom') {
                            productsInChargeLine.forEach(p => {
                                newField = { ...newField, values: { ...newField.values, [p.id]: { ...newField.values[p.id], ...update } } };
                            });
                        } else {
                            productsInChargeLine.forEach(p => {
                                newField = { ...newField, values: { ...newField.values, [p.id]: { ...newField.values[p.id], value: null, formula: null } } };
                            });
                        }
                        newField = { ...newField, values: { ...newField.values, [productId]: { ...newField.values[productId], ...update } } };
                        setField(newField);
                    }

                    if (formula) {
                        // Calculate the field value for this charge line
                        let calculatedValue = null;
                        try {
                            let displayCurrency = calculatedBuyingRates.field.displayCurrency;
                            if (_.isObject(displayCurrency)) {
                                if (displayCurrency.convert) {
                                    displayCurrency = displayCurrency.to;
                                } else {
                                    displayCurrency = null;
                                }
                            }
                            if (!displayCurrency) displayCurrency = currency;
                            const result = calcFieldValue({ 
                                field: { ...calculatedBuyingRates.field, displayCurrency }, 
                                products, 
                                sections, 
                                transportationMethod, 
                                organization, 
                                buyingRates: calculatedBuyingRates.buyingRates, 
                                fieldBuyingRates: calculatedBuyingRates.field.buyingRates, 
                                exchangeRates, 
                                lockedExchangeRates,
                                forceLine: line
                            });
                            calculatedValue = result?.sum;
                        } catch (e) {
                            console.error('Error calculating field value:', e);
                        }

                        const attempt = formulaToParams({ formula });
                        const isUplift = attempt && attempt.buying;
                        const formulaType = isUplift ? 'Uplift' : 'Formula';
                        
                        // Format the calculated value
                        const calculatedValueStr = calculatedValue ? _.map(calculatedValue, (value, curr) => {
                            return currencyToText(curr, true, Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value));
                        }).join(', ') : null;

                        // Get formula explanation for tooltip
                        const formulaExplanation = formulaToReact({ 
                            formula, 
                            currency, 
                            errorValue: formula, 
                            multiplierText: multiplierTextSingular, 
                            sections, 
                            width: 'auto', 
                            sx: {} 
                        });

                        return (
                            <Box key={productId}>
                                {field.type === 'custom' && (
                                    <Typography variant='caption' component='div' sx={{ fontWeight: '800', marginBottom: '12px' }}>
                                        Formula:
                                    </Typography>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {title && <Typography>{title}</Typography>}
                                    {calculatedValueStr ? (
                                        <Tooltip title={formulaExplanation} arrow>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <Typography sx={{ textDecoration: 'underline', display: 'inline' }} variant='body1'>
                                                    {calculatedValueStr} based on {formulaType}
                                                </Typography>
                                                <sup>
                                                    <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '11px', verticalAlign: 'super' }} />
                                                </sup>
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <Typography sx={{ fontSize: '14px', fontStyle: 'italic', color: 'text.secondary' }}>
                                            Calculating...
                                        </Typography>
                                    )}
                                    {(() => {
                                        return (
                                            <>
                                                <Button
                                                    variant='text'
                                                    sx={{ textTransform: 'none', marginLeft: '20px' }}
                                                    onClick={() => setFormulaDialogOpen(isUplift ? `${productId}-uplift` : `${productId}-formula`)}
                                                    startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
                                                >
                                                    {isUplift ? 'Open Uplift Popup' : 'Open Formula Popup'}
                                                </Button>
                                                <Button
                                                    variant='text'
                                                    sx={{ textTransform: 'none' }}
                                                    onClick={() => {
                                                        // Get the calculated value for the current currency
                                                        // If the calculated value exists, use it; otherwise default to 0
                                                        let valueForCurrency = 0;
                                                        if (calculatedValue) {
                                                            // Try to get the value for the current currency first
                                                            if (calculatedValue[currency] !== undefined) {
                                                                valueForCurrency = calculatedValue[currency];
                                                            } else {
                                                                // If current currency not found, use the first available currency value
                                                                const firstCurrency = Object.keys(calculatedValue)[0];
                                                                if (firstCurrency) {
                                                                    valueForCurrency = calculatedValue[firstCurrency];
                                                                }
                                                            }
                                                        }
                                                        // For non-flat fields, divide by multiplier to get per-unit price
                                                        if (field.type !== 'flat' && multiplier && multiplier > 0) {
                                                            valueForCurrency = valueForCurrency / multiplier;
                                                        }
                                                        // Set the value to the calculated result and remove the formula
                                                        updateAllProducts({ 
                                                            formula: null, 
                                                            value: valueForCurrency,
                                                            currency: currency 
                                                        });
                                                    }}
                                                    startIcon={<FontAwesomeIcon icon={faLinkSlash} />}
                                                >
                                                    Break Formula
                                                </Button>
                                            </>
                                        );
                                    })()}
                                </Box>
                                {field.type === 'custom' && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px', marginTop: '16px' }}>
                                        <Typography variant='caption' sx={{ fontWeight: '800' }}>
                                            Calculated for:
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <Typography variant='body1'>Number of items:</Typography>
                                            <TextField
                                                value={field.values?.custom?.multiplier}
                                                onChange={(e) => updateAllProducts({ multiplier: parseFloatOrString(e.target.value) })}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <Typography variant='body1'>Item Description (eg. Invoices, Packages, etc.):</Typography>
                                            <TextField
                                                value={field.values?.custom?.multiplierText}
                                                onChange={(e) => updateAllProducts({ multiplierText: e.target.value })}
                                            />
                                        </Box>
                                    </Box>
                                )}
                                <FormulaEditorModal
                                    open={formulaDialogOpen === `${productId}-uplift`}
                                    onClose={() => setFormulaDialogOpen(null)}
                                    value={formula || ''}
                                    onDone={result => {
                                        // Handle both string (formula) and object ({ formula, currency }) formats
                                        const formulaValue = typeof result === 'string' ? result : result.formula;
                                        const formulaCurrency = typeof result === 'object' && result.currency ? result.currency : (currency || organization.defaults?.currency);
                                        updateAllProducts({ currency: formulaCurrency, value: 0, formula: formulaValue });
                                        setFormulaDialogOpen(null);
                                    }}
                                    sections={sections}
                                    noTax={field.type !== 'flat'}
                                    withBuyingMargin={true}
                                    initialTab={0}
                                    hideTabs={true}
                                    initialFixedOrTier='buying-margin'
                                    currency={currency || organization.defaults?.currency}
                                    organization={organization}
                                    showLCLWM={productsInChargeLine.some(p => p.type === 'LCL')}
                                />
                                <FormulaEditorModal
                                    open={formulaDialogOpen === `${productId}-formula`}
                                    onClose={() => setFormulaDialogOpen(null)}
                                    value={formula || ''}
                                    onDone={formula => {
                                        updateAllProducts({ currency: currency || organization.defaults?.currency, value: 0, formula });
                                        setFormulaDialogOpen(null);
                                    }}
                                    sections={sections}
                                    noTax={field.type !== 'flat'}
                                    withBuyingMargin={true}
                                    initialTab={0}
                                    hideBuyingMarginOption={true}
                                    currency={currency || organization.defaults?.currency}
                                    showLCLWM={productsInChargeLine.some(p => p.type === 'LCL')}
                                />
                            </Box>
                        );
                    }

                    return (
                        <Box key={productId} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Typography>{title}</Typography>
                            <TextField
                                sx={{ width: '100px', marginTop: '10px', marginLeft: title ? '18px' : '0px'}}
                                variant='standard'
                                value={_.isUndefined(value) ? 0 : value}
                                onChange={(e) => updateAllProducts({ currency: currency || organization.defaults?.currency, value: e.target.value })}
                                onBlur={() => {
                                    if (!value) updateAllProducts({ currency: currency || organization.defaults?.currency, value: 0 });
                                }}
                                margin='normal'
                                label={field.type === 'custom' ? 'Price' : ''}
                                InputLabelProps={field.type === 'custom' ? {
                                    shrink: true
                                } : {}}
                            />
                            <Button
                                sx={{ minWidth: '0px', padding: '0px', mt: field.type === 'custom' ? '16px' : '0px' }}
                                variant='text'
                                onClick={() => setCurrencyDialogOpen(productId)}
                            >
                                {currencyToText(currency || organization.defaults?.currency, false, '')}
                            </Button>
                            {multiplierText && field.type !== 'custom' && (
                                <Typography variant='body1'>&times; {multiplier} {multiplierText}</Typography>
                            )}
                            {field.type === 'custom' && (
                                <>
                                    <Typography variant='body1' sx={{ marginTop: '10px', marginLeft: '18px'}}>&times;</Typography>
                                    <TextField
                                        sx={{ width: '100px', marginTop: '10px', marginLeft: '18px'}}
                                        variant='standard'
                                        value={field.values?.custom?.multiplier}
                                        onChange={(e) => updateAllProducts({ multiplier: parseFloatOrString(e.target.value) })}
                                        margin='normal'
                                        label='# Items'
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                    <TextField
                                        sx={{ width: '300px', marginTop: '10px', marginLeft: '18px'}}
                                        variant='standard'
                                        value={field.values?.custom?.multiplierText}
                                        onChange={(e) => updateAllProducts({ multiplierText: e.target.value })}
                                        margin='normal'
                                        label='Item Description (eg. Invoices, Packages, etc.)'
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                </>
                            )}
                            <Box sx={{ display: 'flex', gap: '6px', marginLeft: '20px', alignItems: 'center' }}>
                                <Chip
                                    label='Add Uplift'
                                    color='primary'
                                    onClick={() => setFormulaDialogOpen(`${productId}-uplift`)}
                                    sx={{ cursor: 'pointer', height: '32px' }}
                                />
                                <Chip
                                    label='Add Formula'
                                    color='default'
                                    onClick={() => setFormulaDialogOpen(`${productId}-formula`)}
                                    sx={{ cursor: 'pointer', height: '32px' }}
                                />
                            </Box>
                            <CurrencyDialog
                                open={currencyDialogOpen === productId}
                                onClose={() => setCurrencyDialogOpen(null)}
                                initialCurrency={currency || organization.defaults?.currency}
                                onCurrency={currency => {
                                    updateAllProducts({ currency });
                                    setCurrencyDialogOpen(null);
                                }}
                                defaultCurrency={organization.defaults?.currency}
                                extraCurrencies={organization.defaults?.extraCurrencies}
                            />
                            <FormulaEditorModal
                                open={formulaDialogOpen === `${productId}-uplift`}
                                onClose={() => setFormulaDialogOpen(null)}
                                value={formula || ''}
                                onDone={result => {
                                    // Handle both string (formula) and object ({ formula, currency }) formats
                                    const formulaValue = typeof result === 'string' ? result : result.formula;
                                    const formulaCurrency = typeof result === 'object' && result.currency ? result.currency : (currency || organization.defaults?.currency);
                                    updateAllProducts({ currency: formulaCurrency, value: 0, formula: formulaValue });
                                    setFormulaDialogOpen(null);
                                }}
                                sections={sections}
                                noTax={field.type !== 'flat'}
                                withBuyingMargin={true}
                                initialTab={0}
                                hideTabs={true}
                                initialFixedOrTier='buying-margin'
                                currency={currency || organization.defaults?.currency}
                                organization={organization}
                            />
                            <FormulaEditorModal
                                open={formulaDialogOpen === `${productId}-formula`}
                                onClose={() => setFormulaDialogOpen(null)}
                                value={formula || ''}
                                onDone={formula => {
                                    updateAllProducts({ currency: currency || organization.defaults?.currency, value: 0, formula });
                                    setFormulaDialogOpen(null);
                                }}
                                sections={sections}
                                noTax={field.type !== 'flat'}
                                withBuyingMargin={true}
                                initialTab={0}
                                hideBuyingMarginOption={true}
                                currency={currency || organization.defaults?.currency}
                            />
                        </Box>
                    );
                })}
            </Box>
        </Box>
    )
}

function FieldSettingBuyingRates(props) {
    const { suppliers, products, transportationMethod, organization, rows, setRows, buyingRates, newBuyingRates, setNewBuyingRates } = props;

    // For drag and drop
    const [ dropOver, setDropOver ] = useState(null);
    const first = useRef();
    const setDropOverRef = useRef();
    const onAddBuyingRateRef = useRef();
    const productsRef = useRef();
    setDropOverRef.current = setDropOver;
    productsRef.current = products;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['RateCardSingleRate', 'RateCardSingleSection', 'RateCard'], 
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            setDropOverRef.current(monitor.canDrop(item, monitor) ? 'candrop' + (item.rate.type === 'tariff' ? '-tariff' : '') : 'cantdrop' + (item.rate.type === 'tariff' ? '-tariff' : ''));
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    setDropOverRef.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {

            const fields = item.section ? item.section.offers[0].fields :
                item.field ? [item.field] :
                    item.rate.offer.sections.map(s => s.offers[0].fields.map(f => ({ ...f, title: `${s.title} / ${f.title}` }))).flat();

            onAddBuyingRateRef.current({ rate: item.rate, rateFields: fields.filter(f => f.type !== 'string') });
        },
        canDrop: (item, monitor) => {
            return !_.isEmpty(findMatchingProducts({ product: item.rate.product, products: productsRef.current }));
        },
    }));

    function onAddBuyingRate({ rate, rateFields }) {
        if (!rate && _.isEmpty(rateFields)) {
            setRows([...rows, { type: 'buyingRate', id: uuidv4(), buyingRate: _.last(rows.filter(r => r.type === 'buyingRate'))?.buyingRate, fieldId: null }]);
            return;
        }
        // Convert rate from KG to LBS if needed
        const convertedRate = rate ? convertRateKgToLbsIfNeeded({ rate, opportunityProducts: products }) : null;
        if (convertedRate && !newBuyingRates?.find(br => br.id === convertedRate.id) && !buyingRates?.find(br => br.id === convertedRate.id)) {
            setNewBuyingRates([ ...newBuyingRates, convertedRate ]);
        }
        setRows([ ...rows, ...rateFields.map(field => ({ type: 'buyingRate', id: uuidv4(), buyingRate: convertedRate, fieldId: field.id })) ]);
    }
    onAddBuyingRateRef.current = onAddBuyingRate;

    function onAddManualBuyingRate() {
        const lastRow = _.last(rows);
        if (lastRow?.type === 'manual') {
            setRows([...rows, { ...lastRow, id: uuidv4(), value: null, title: null }]);
        } else {
            setRows([...rows, { type: 'manual', id: uuidv4(), fieldType: 'flat', product: products[0], currency: organization.defaults?.currency }]);
        }
    }

    const setRow = (row) => setRows(_.cloneDeep(rows).map(r => r.id === row.id ? row : r));

    return (
        <Box ref={drop} sx={{ minHeight: '100%' }}>
            {rows.map(row => (
                <Box key={row.id}>
                    {row.type === 'manual' && (
                        <FieldSettingBuyingRateManual
                            row={row}
                            setRow={setRow}
                            suppliers={suppliers}
                            products={products}
                            transportationMethod={transportationMethod}
                            organization={organization}
                            onDeleteRow={() => setRows(_.cloneDeep(rows).filter(r => r.id !== row.id))}
                        />
                    )}
                    {row.type === 'buyingRate' && (
                        <FieldSettingBuyingRate
                            row={row}
                            setRow={setRow}
                            buyingRates={[...(buyingRates || []), ...(newBuyingRates || [])]}
                            organization={organization}
                            onDeleteRow={() => setRows(_.cloneDeep(rows).filter(r => r.id !== row.id))}
                            transportationMethod={transportationMethod}
                        />
                    )}
                </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', paddingTop: _.isEmpty(rows) ? '0px' : '12px' }}>
                <Button 
                    sx={{ 
                        flex: 1,
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        border: '1px dashed #3b82f6',
                        color: '#3b82f6',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: '#eff6ff',
                            border: '1px solid #3b82f6'
                        },
                        '&:disabled': {
                            border: '1px dashed #cbd5e1',
                            color: '#94a3b8'
                        }
                    }} 
                    disabled={_.isEmpty(buyingRates)} 
                    startIcon={<FontAwesomeIcon icon={faMagnifyingGlassDollar} />} 
                    onClick={() => onAddBuyingRate({ rate: null, rateFields: [] })}
                >
                    Add Buying Rate
                </Button>
                <Button 
                    sx={{ 
                        flex: 1,
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        border: '1px dashed #3b82f6',
                        color: '#3b82f6',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: '#eff6ff',
                            border: '1px solid #3b82f6'
                        }
                    }} 
                    startIcon={<FontAwesomeIcon icon={faPenToSquare} />} 
                    onClick={onAddManualBuyingRate}
                >
                    Add Manual Buying Rate
                </Button>
            </Box>
            {dropOver === 'candrop' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Drop your rate here to add it to the buying rates.
                </Box>
            )}
            {dropOver === 'candrop-tariff' && (
                <Box sx={{ position: 'absolute', background: grey[800], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Drop your tariff here to set the price.
                </Box>
            )}
            {dropOver === 'cantdrop' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Rate is not for a product included in the opportunity, or field types do not match.
                </Box>
            )}
            {dropOver === 'cantdrop-tariff' && (
                <Box sx={{ position: 'absolute', background: red[500], opacity: 0.8, top: '0px', left: '0px', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center' }}>
                    Tariff is not for a product included in the opportunity, or field types do not match.
                </Box>
            )}
        </Box>
    )
}

function FieldSettingBuyingRateManual(props) {
    const { row, setRow, suppliers, products, transportationMethod, organization, onDeleteRow } = props;
    const fieldTypes = FieldTypes[transportationMethod]?.filter(ft => (transportationMethod === 'air' || !ft.isAggregated) && (!_.isFunction(ft.enabled) || ft.enabled({ organization, forBuyingRate: true })));
    const [ currencyDialogOpen, setCurrencyDialogOpen ] = useState(false);

    const supplier = row.supplier;
    const setSupplier = (s) => setRow({ ...row, supplier: s });
    const product = row.product;
    const setProduct = (p) => setRow({ ...row, product: p });
    const fieldType = row.fieldType;
    const setFieldType = (ft) => setRow({ ...row, fieldType: ft });

    // Filter suppliers - include all suppliers (not filtering by email).
    // Stable reference + deduped by name so MUI's Autocomplete doesn't get stale state
    // when the parent re-renders during typing.
    const availableSuppliers = useMemo(() => {
        return _.uniqBy((suppliers || []).filter(s => s && s.name), s => (s.uniqueId || s.name.toLowerCase()));
    }, [suppliers]);

    // Plain substring filter on supplier name only — avoids MUI's default filter which
    // can cache stale results or match on stringified option internals.
    const supplierFilterOptions = (options, { inputValue }) => {
        const q = (inputValue || '').trim().toLowerCase();
        if (!q) return options;
        return options.filter(o => (o?.name || '').toLowerCase().includes(q));
    };

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
                <Box sx={{ flex: '0 0 17%', minWidth: 0 }}>
                <Autocomplete
                    fullWidth
                    size='small'
                    options={availableSuppliers}
                    filterOptions={supplierFilterOptions}
                    getOptionLabel={(option) => option?.name || ''}
                    renderOption={(props, option) => (
                        <li {...props} key={option?.uniqueId || option?.name}>{option?.name}</li>
                    )}
                    value={supplier}
                    onChange={(e, newValue) => setSupplier(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label='Supplier'
                            placeholder='Pick Supplier...'
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#ffffff',
                                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                                    '& fieldset': {
                                        borderColor: '#e2e8f0',
                                        borderWidth: '1px'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#e2e8f0'
                                    },
                                    '&.Mui-focused': {
                                        '& fieldset': {
                                            borderColor: '#3b82f6',
                                            borderWidth: '1px'
                                        },
                                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)'
                                    }
                                }
                            }}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: supplier ? params.InputProps.startAdornment : (
                                    <InputAdornment position='start'>
                                        <FontAwesomeIcon icon={faSearch} />
                                    </InputAdornment>
                                )
                            }}
                        />
                    )}
                    isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return false;
                        // Match by uniqueId if both have it, otherwise match by name
                        if (option.uniqueId && value.uniqueId) {
                            return option.uniqueId === value.uniqueId;
                        }
                        return option.name?.toLowerCase() === value.name?.toLowerCase();
                    }}
                />
                </Box>
                <Box sx={{ flex: '0 0 15%', minWidth: 0 }}>
                    <FormControl fullWidth>
                        <InputLabel>Product</InputLabel>
                        <Select
                            fullWidth
                            label='Product'
                            value={product?.id || ''}
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0', borderWidth: '1px' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6', borderWidth: '1px' },
                                '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)' },
                                backgroundColor: '#ffffff',
                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
                            }}
                            inputProps={{ sx: { '>div': { fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } } }}
                            onChange={(e) => {
                                const _product = products.find(p => p.id === e.target.value);
                                setProduct(_product);
                            }}
                        >
                            {products.map(p => <MenuItem key={p.id} value={p.id}>
                                {getProductShortDescription({ product: p })}
                            </MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ flex: '0 0 12%', minWidth: 0 }}>
                <TextField
                    fullWidth
                    sx={{
                        marginTop: '10px',
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#ffffff',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                            '& fieldset': { borderColor: '#e2e8f0', borderWidth: '1px' },
                            '&:hover fieldset': { borderColor: '#e2e8f0' },
                            '&.Mui-focused': {
                                '& fieldset': { borderColor: '#3b82f6', borderWidth: '1px' },
                                boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)'
                            }
                        }
                    }}
                    value={row.title || ''}
                    onChange={(e) => setRow({ ...row, title: e.target.value })}
                    margin='normal'
                    label='Charge name'
                />
                </Box>
                <Box sx={{ flex: '0 0 17%', minWidth: 0 }}>
                <FormControl fullWidth>
                    <InputLabel>Priced per</InputLabel>
                    <Select
                        fullWidth
                        label='Priced per'
                        value={fieldType}
                        onChange={(e) => setFieldType(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0', borderWidth: '1px' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6', borderWidth: '1px' },
                            '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)' },
                            backgroundColor: '#ffffff',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
                        }}
                        inputProps={{ sx: { fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                    >
                        {fieldTypes.filter(ft => ft.value !== 'custom' && ft.value !== 'string').map(ft => (_.isFunction(ft.enabled) && !ft.enabled({ organization, forBuyingRate: true })) ? null : <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>)}
                    </Select>
                </FormControl>
                </Box>
                <Box sx={{ flex: 1, minWidth: '12px' }} />
                <Box sx={{ flex: '0 0 22%', minWidth: 0 }}>
                <TextField
                    fullWidth
                    sx={{
                        marginTop: '10px',
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#ffffff',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
                            '& fieldset': { borderColor: '#e2e8f0', borderWidth: '1px' },
                            '&:hover fieldset': { borderColor: '#e2e8f0' },
                            '&.Mui-focused': {
                                '& fieldset': { borderColor: '#3b82f6', borderWidth: '1px' },
                                boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)'
                            }
                        }
                    }}
                    // A cost with no amount of its own is not a cost of zero. Showing 0 here made a
                    // charge driven entirely by a formula read as free, which is how a quote came
                    // to total its formula's minimum while every box on this screen said 0.
                    value={_.isUndefined(row.value) || row.value === null ? '' : row.value}
                    // Typing an amount means that amount. A formula replaces the value outright,
                    // so keeping one made the number typed here do nothing at all — the box took
                    // the 0 and the charge carried on at its minimum.
                    onChange={(e) => setRow({ ...row, value: e.target.value, formula: null })}
                    margin='normal'
                    InputProps={{
                        endAdornment: <InputAdornment position='end'>
                            <Button
                                variant='text'
                                onClick={() => setCurrencyDialogOpen(true)}
                            >
                                {currencyToText(row.currency || organization.defaults?.currency, false, '')}
                            </Button>
                        </InputAdornment>
                    }}
                />
                {row.formula && (
                    <Box sx={{ fontSize: '11px', color: '#64748b', marginTop: '2px', fontFamily: 'Poppins, sans-serif' }}>
                        {formulaToReact({ formula: row.formula, errorValue: 'Formula', currency: row.currency || organization.defaults?.currency, multiplierText: 'shipment', sections: [], width: 'auto' })}
                    </Box>
                )}
                </Box>
                <Box sx={{ flex: '0 0 12%', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#64748b', fontSize: '13px', fontFamily: 'Poppins, sans-serif', display: 'flex', alignItems: 'center' }}>
                    {(() => {
                        if (!product) return '';
                        if (fieldType === 'flat') return '× 1 shipment';
                        if (fieldType === 'per-unit-weight' || fieldType === 'per-unit-type-weight') {
                            const w = calcWeight({ product });
                            return `× ${(w.val || 0).toLocaleString()} ${w.postfix} (gw)`;
                        }
                        if (fieldType === 'per-unit' || fieldType === 'per-unit-type') {
                            const isAir = product.type === 'Air Freight' || product.type === 'Air freight';
                            if (isAir) {
                                const cw = calcChargeableWeight({ product, transportationMethod: 'air', allProducts: products, round: organization?.airRoundChargeableWeight });
                                if (!cw) return '';
                                return `× ${(cw.val || 0).toLocaleString()} ${cw.postfix} (cw)`;
                            }
                            const isWM = product.type === 'LCL' || (product.type || '').startsWith('By Volume or Weight') || (product.type || '').startsWith('By Dimensions or Weight');
                            if (isWM) {
                                const wm = calcWeightOrMeasurment({ product, transportationMethod: 'sea', allProducts: products });
                                if (!wm) return '';
                                return `× ${(wm.val || 0).toLocaleString()} ${wm.postfix}`;
                            }
                            return `× ${(product.quantity || 1).toLocaleString()} ${product.type || 'unit'}`;
                        }
                        return '';
                    })()}
                </Box>
                <Button
                    sx={{ minWidth: '0px', padding: '0px', marginLeft: '36px' }}
                    variant='text'
                    onClick={onDeleteRow}
                    color='error'
                >
                    <FontAwesomeIcon icon={faTrash} />
                </Button>
            </Box>
            <CurrencyDialog
                open={currencyDialogOpen}
                onClose={() => setCurrencyDialogOpen(null)}
                initialCurrency={row.currency || organization.defaults?.currency}
                onCurrency={currency => {
                    setRow({ ...row, currency });
                    setCurrencyDialogOpen(false);
                }}
                defaultCurrency={organization.defaults?.currency}
                extraCurrencies={organization.defaults?.extraCurrencies}
            />
        </>
    )
}

function FieldSettingBuyingRate(props) {
    const { row, setRow, buyingRates, organization, onDeleteRow, transportationMethod } = props;

    const buyingRate = row.buyingRate;
    const setBuyingRate = (br) => setRow({ ...row, buyingRate: br });
    const fieldId = row.fieldId || '';
    const setField = (f) => setRow({ ...row, fieldId: f, fieldType: null });
    const field = _(buyingRate?.offer.sections).map(s => s.offers).flatten().map(o => o.fields).flatten().find(f => f.id === fieldId);

    const rateTransportationMethod = buyingRate?.transportationMethod || transportationMethod;
    // A cost is calculated the way its supplier priced it. This was briefly changeable here, and
    // the change was written back into the quote's copy of the rate; it is gone again because it
    // rewrote uploaded rates' cost types in place and could not be undone from the screen.
    const fieldType = buyingRate && field ? FieldTypes[rateTransportationMethod].find(ft => ft.value === field.type) : null;
    const chargeLine = fieldType?.getChargeLines ? fieldType.getChargeLines({ field, products: [buyingRate.product], organization }) : null;
    const { multiplierText, fieldValueId } = (chargeLine && chargeLine[0]) || { multiplierText: null, fieldValueId: null };

    // Filed under the key the supplier's own calculation type gives it, with a fallback for a
    // rate whose amount sits under some other key.
    const stored = (fieldValueId && field?.values?.[fieldValueId])
        || _.values(field?.values || {}).find(v => v && (v.value !== null && v.value !== undefined && v.value !== '' || v.formula))
        || {};
    const value = stored.value ?? null;
    const currency = stored.currency || organization.defaults?.currency;
    const formula = stored.formula || null;

    const fraction = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 0px' }}>
            <FormControl fullWidth>
                <InputLabel>Buying Rate</InputLabel>
                <Select
                    fullWidth
                    placeholder='Pick Buying Rate...'
                    label='Buying Rate'
                    value={buyingRate?.id}
                    onChange={(e) => setBuyingRate(buyingRates.find(br => br.id === e.target.value))}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0',
                            borderWidth: '1px'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                            borderWidth: '1px'
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)'
                        },
                        backgroundColor: '#ffffff',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
                    }}
                    inputProps={{
                        sx: {
                            '>div': {
                                fontSize: '13px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }
                        }
                    }}
                >
                    {buyingRates.map(br => <MenuItem key={br.id} value={br.id}>{br.supplier.organization} - {br.product.type}</MenuItem>)}
                </Select>
            </FormControl>
            <FormControl fullWidth>
                <InputLabel>Field</InputLabel>
                <Select
                    fullWidth
                    placeholder='Pick Field...'
                    label='Field'
                    value={fieldId}
                    disabled={!buyingRate}
                    onChange={(e) => setField(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0',
                            borderWidth: '1px'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                            borderWidth: '1px'
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), inset 0 1px 2px rgba(0,0,0,0.02)'
                        },
                        backgroundColor: '#ffffff',
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)'
                    }}
                    inputProps={{
                        sx: {
                            '>div': {
                                fontSize: '13px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }
                        }
                    }}
                >
                    {_.map(buyingRate?.offer.sections, s => s.offers.map(o => o.fields.map(f => <MenuItem key={f.id} value={f.id}>{f.title}</MenuItem>))).flat().flat()}
                </Select>
            </FormControl>
            {multiplierText && (
                <Box sx={{ fontSize: '11px', marginLeft: '24px' }}>
                    {!formula && (
                        <>
                            {currencyToText(currency, true, fraction.format(value))}
                        </>
                    )}
                    {formula
                        ? formulaToReact({
                            formula: formula,
                            currency: currency,
                            multiplierText,
                            sections: buyingRate.offer.sections,
                            errorValue: <Tooltip title={formula}><i style={{ borderBottom: '1px dotted' }}>Formula</i></Tooltip>
                        }) : null
                    }
                    {multiplierText ? ` / ${pluralize(multiplierText, 1)}` : ''}
                </Box>
            )}
            <Button
                sx={{ minWidth: '0px', padding: '0px', marginLeft: '36px' }}
                variant='text'
                onClick={onDeleteRow}
                color='error'
            >
                <FontAwesomeIcon icon={faTrash} />
            </Button>
        </Box>
    )
}

function rowsToRates({ rows, field, buyingRates, offers, transportationMethod, products, organization }) {
    let newBuyingRates = _.cloneDeep(buyingRates);
    const newFieldBuyingRates = [];

    rows.forEach(row => {

        if ((row.type === 'buyingRate') && (!row.buyingRate || !row.fieldId)) return;
        if ((row.type === 'manual') && (!row.product || !row.fieldType || (!row.value && !row.formula) || !row.currency)) return;

        // For each manual row, find a matching existing manual buying rate
        let buyingRate = newBuyingRates.find(br => {
            if (row.type === 'manual') {
                if (!isManualBuyingRate(br)) return false;
                
                // First check if products match
                if (_.isEmpty(findMatchingProducts({ product: br.product, products: [row.product] }))) return false;
                
                // Then check supplier matching - prioritize uniqueId matching
                const brUniqueId = br.supplier?.uniqueId?.replace(/:/g, '').trim() ? br.supplier.uniqueId : null;
                const rowUniqueId = row.supplier?.uniqueId?.replace(/:/g, '').trim() ? row.supplier.uniqueId : null;
                
                // If both have uniqueIds, they must match
                if (brUniqueId && rowUniqueId) {
                    if (brUniqueId !== rowUniqueId) return false;
                } else if (brUniqueId || rowUniqueId) {
                    // If only one has a uniqueId, they don't match
                    return false;
                }
                
                // If uniqueIds match (or both are missing), check by name
                const brName = br.supplier?.organization?.toLowerCase();
                const rowName = row.supplier?.name?.toLowerCase();
                if (brName !== rowName) return false;
                
                return true;
            }
            if (row.type === 'buyingRate') {
                return br.id === row.buyingRate.id;
            }
        })

        // If not found, create a new manual buying rate or drop the row
        if (!buyingRate && row.type === 'buyingRate') {
            // The rate a row was built from can be taken away while this dialog sits open: the
            // cost centre deletes the master cost, and the rows here still describe the field as
            // it was a moment ago. This used to push the row's own stale copy of the rate back in
            // without keeping hold of it, then read the sections off nothing - which took the
            // whole quote screen down. A cost whose rate has been deleted contributes nothing and
            // must not come back on save, so the row goes.
            return;
        } else if (!buyingRate && row.type === 'manual') {
            buyingRate = {
                id: uuidv4(),
                platform: -2,
                product: (() => {
                    const p = _.pick(row.product, ['id', 'type', 'quantity', 'dangerous', 'weight', 'volume', 'kgOrLbs', 'cmOrInches', 'width', 'height', 'length', 'chargeableWeight', 'celsiusOrFahrenheit', 'temperature', 'commodity', 'calculations', 'densityRatio']);
                    // Ensure LCL rate products have a default calculation for W/M computation
                    if (p.type === 'LCL' && (!p.calculations || p.calculations.length === 0)) {
                        p.calculations = [{ id: 'default', method: 'volume', volume: 1, weight: 1000, quantity: 1 }];
                        p.kgOrLbs = p.kgOrLbs || 'kg';
                        p.cmOrInches = p.cmOrInches || 'cm';
                    }
                    return p;
                })(),
                transportationMethod,
                supplier: {
                    organization: row.supplier?.name,
                    uniqueId: row.supplier?.uniqueId,
                    email: row.supplier?.email,
                },
                offer: {
                    sections: [{
                        offers: [{
                            fields: []
                        }]
                    }]
                }
            };
            newBuyingRates.push(buyingRate);
        }

        // Find the field in the buying rate
        let buyingRateField = (() => {
            if (row.type === 'buyingRate') {
                return _(buyingRate.offer.sections)
                    .map(s => s.offers).flatten()
                    .map(o => o.fields).flatten()
                    .find(f => f.id === row.fieldId);
            }
            if (row.type === 'manual') {
                return buyingRate.offer.sections[0].offers[0].fields.find(f => f.id === row.id);
            }
        })();
        
        // If not found, create a new field
        if (!buyingRateField && row.type === 'buyingRate') {
            console.error('Field not found in buying rate, this should never happen');
            return;
        }

        if (row.type === 'manual') {
            if (!buyingRateField) {
                buyingRateField = {
                    id: uuidv4(),
                    title: row.title || '',
                    type: row.fieldType,
                    values: {}
                };
                buyingRate.offer.sections[0].offers[0].fields.push(buyingRateField);
            }

            // Always sync field type and title from the row (user may have changed them)
            buyingRateField.type = row.fieldType;
            buyingRateField.title = row.title || buyingRateField.title || '';

            // Set the field value
            const fieldType = FieldTypes[transportationMethod]?.find(ft => ft.value === row.fieldType);
            const chargeLines = fieldType?.getChargeLines ? fieldType.getChargeLines({ field: buyingRateField, products: [buyingRate.product], organization }) : [];
            const fieldValueId = chargeLines ? chargeLines[0].fieldValueId : row.fieldType;
            buyingRateField.values[fieldValueId] = {
                formula: row.formula || null,
                value: row.value,
                currency: row.currency || organization.defaults?.currency,
            };
        }

        const _products = (() => {
            if (row.type === 'buyingRate') {
                return findMatchingProducts({ product: row.buyingRate.product, products: products });
            }
            if (row.type === 'manual') {
                return [row.product];
            }
        })();

        if (_products && _products.length > 0) {
            _products.forEach(product => {
                newFieldBuyingRates.push({
                    rateId: buyingRate.id,
                    fieldId: buyingRateField.id,
                    opportunityProductId: product.id,
                });
            });
        }
    });

    // Preserve orphaned buying-rate refs — costs whose product type no longer matches the
    // opportunity product they point to. These are intentionally hidden from the editable rows
    // (mirroring the cost center), so without re-adding them here, saving the dialog would drop
    // the refs and the cleanup below would delete the underlying cost, losing it from Alternative
    // Costs. They remain unlinked: the read-side type guard keeps them out of the field's totals.
    const orphanedRefs = (field.buyingRates || []).filter(br => {
        const rate = newBuyingRates.find(r => r.id === br.rateId);
        const product = products.find(p => p.id === br.opportunityProductId);
        return rate && product && rate.product?.type && product?.type
            && normalizeProductType(rate.product.type) !== normalizeProductType(product.type);
    });
    newFieldBuyingRates.push(...orphanedRefs);

    // Update the offers with the new field buying rates
    const newOffers = _.cloneDeep(offers || []);
    const newField = { ...field, buyingRates: newFieldBuyingRates };
    newOffers.forEach(o => 
        o.sections.forEach(s => 
            s.offers.forEach(o => 
                o.fields = o.fields.map(f => f.id === field.id ? newField : f)
            )
        )
    );
    
    // Clean up only buying rate fields that were removed from the current field.
    // Do NOT touch buying rate fields that belong to other fields or standalone Cost Center costs.
    const originalRefs = (field.buyingRates || []).map(br => `${br.rateId}|${br.fieldId}`);
    const newRefs = new Set(newFieldBuyingRates.map(br => `${br.rateId}|${br.fieldId}`));
    for (const ref of originalRefs) {
        if (newRefs.has(ref)) continue; // still referenced by current field
        const [rateId, brFieldId] = ref.split('|');
        const br = newBuyingRates.find(b => b.id === rateId);
        if (!br || !isManualBuyingRate(br)) continue;
        // Check if any OTHER offer field still references this buying rate field
        const stillReferenced = _.some(newOffers, o =>
            _.some(o.sections, s =>
                _.some(s.offers, o =>
                    _.some(o.fields, _f =>
                        _f.id !== field.id &&
                        _.some(_f.buyingRates, fbr => fbr.rateId === rateId && fbr.fieldId === brFieldId)
                    )
                )
            )
        );
        if (!stillReferenced) {
            br.offer.sections.forEach(s => s.offers.forEach(o => {
                o.fields = o.fields.filter(f => f.id !== brFieldId);
            }));
        }
    }

    newBuyingRates = newBuyingRates.filter(br => {
        if (!isManualBuyingRate(br)) return true;
        br.offer.sections = br.offer.sections.filter(s => {
            s.offers = s.offers.filter(o => !_.isEmpty(o.fields));
            return !_.isEmpty(s.offers);
        });
        return !_.isEmpty(br.offer.sections);
    });

    return {
        buyingRates: newBuyingRates,
        offers: newOffers,
        field: newField,
    };
}

export { rowsToRates };
